import React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import Button from '../_custom/Button/Button';
import { formatDateInIST, DATE_FORMATS } from '../../utils/dateUtils';
import styles from './ConsentAuditDrawer.module.scss';
import { ConsentAuditDrawerProps, ConsentHistoryEntry } from './types';
import { CONSENT_PANEL_EMPTY_STATE, CONSENT_PANEL_LOAD_FAILED } from './constants';

const ConsentAuditRow: React.FC<{ entry: ConsentHistoryEntry }> = ({ entry }) => {
    // Pinned to IST, not the viewer's machine timezone — the downloadable PDF (formatIst /
    // ConsentAuditTrailPdfBuilder) hardcodes Asia/Kolkata and labels every timestamp " IST", so
    // this panel must show the same clock time a user would see comparing the two.
    const formattedTime = formatDateInIST(entry.consentedAt, DATE_FORMATS.HISTORY_DRAWER);

    return (
        <div className={styles.ConsentRow}>
            <div className={styles.ConsentRowLeft}>
                <span className={styles.GstinLabel}>
                    {entry.gstin}
                    {entry.state ? ` (${entry.state})` : ''}
                </span>
            </div>
            <div className={styles.ConsentRowRight}>{formattedTime}</div>
        </div>
    );
};

const DownloadAuditTrailButton: React.FC<{ onClick: () => void; disabled: boolean }> = ({ onClick, disabled }) => (
    <Button
        type="button"
        text=""
        className={styles.DownloadButton}
        aria-label="Download audit trail"
        icon={<DownloadForOfflineRoundedIcon className={styles.DownloadIcon} sx={{ color: 'inherit' }} />}
        onClick={onClick}
        disabled={disabled}
    />
);

const ConsentAuditDrawer: React.FC<ConsentAuditDrawerProps> = ({
    open,
    onClose,
    loading,
    loadFailed,
    entries,
    orgName,
    onDownload,
    downloading,
}) => {
    if (!open) return null;

    // Portalled to document.body: GstOverview's `.GstPage` sets `isolation: isolate` (needed to
    // keep pinned table columns under the sticky sub-tab bar), which confines this drawer's fixed
    // backdrop to that stacking context instead of the root — the dashboard header then paints
    // over it. Rendering outside the React tree's DOM position sidesteps the stacking context
    // entirely, the same way BottomDrawer and Tooltip's portal mode already do.
    return createPortal(
        <div className={styles.DrawerLayer}>
            <SideDrawer
                width="40%"
                onClose={onClose}
                // The close glyph fills its own box, while the download beside it is a MUI icon whose
                // box carries extra room around the glyph. Nudged apart so both sit on the header's
                // text line — SideDrawer's own prop, so no other drawer is affected.
                iconStyle={{ position: 'relative', top: '2px' }}
                headerActions={<DownloadAuditTrailButton onClick={onDownload} disabled={downloading || loading || entries.length === 0} />}
            >
                <div className={styles.DrawerHeader}>
                    <h2 className={clsx(styles.DrawerTitle, { [styles.DrawerTitleNoDivider]: !orgName })}>GST Consent Log</h2>
                    {orgName && <p className={styles.CompanyName}>{orgName}</p>}
                </div>

                <div className={styles.DrawerContent}>
                    {loading ? (
                        <div className={styles.LoadingState}>
                            <LoadingSpinner height="2.5rem" color="var(--primary-text-colour)" />
                        </div>
                    ) : loadFailed ? (
                        <div className={styles.EmptyState}>{CONSENT_PANEL_LOAD_FAILED}</div>
                    ) : entries.length === 0 ? (
                        <div className={styles.EmptyState}>{CONSENT_PANEL_EMPTY_STATE}</div>
                    ) : (
                        <div className={styles.Timeline}>
                            {entries.map((entry, index) => (
                                <ConsentAuditRow key={`${entry.gstin}-${entry.consentedAt}-${index}`} entry={entry} />
                            ))}
                        </div>
                    )}
                </div>
            </SideDrawer>
        </div>,
        document.body,
    );
};

export default ConsentAuditDrawer;
